import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Loader2, Check, Lock, ArrowRight, FileText, Download, CircleDollarSign,
  ShieldCheck, RefreshCw, Layers, ListChecks, Ban, HelpCircle, HeartPulse, Mic,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader } from "@/components/Common";
import { ModelPicker } from "@/components/ModelPicker";
import { api } from "@/lib/api";
import { downloadServerFile } from "@/lib/exporter";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const PRODUCT_LABEL = {
  landing_page: "Landing Page", business_website: "Business Website",
  commerce_site: "Commerce Site", full_stack_app: "Full-Stack App",
};

function Section({ icon: Icon, title, items, locked }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border border-brand-line bg-white p-5 shadow-soft">
      <p className="flex items-center gap-2 text-sm font-semibold text-brand-text"><Icon size={15} className="text-brand" /> {title}</p>
      <ul className={`mt-3 space-y-1.5 ${locked ? "pointer-events-none select-none blur-sm" : ""}`}>
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="mt-0.5 shrink-0 text-emerald-500" /> {it}</li>
        ))}
      </ul>
    </div>
  );
}

export default function BuildStudio() {
  const navigate = useNavigate();
  const location = useLocation();
  const [bp, setBp] = useState(null);
  const [paid, setPaid] = useState(false);
  const [pricing, setPricing] = useState([]);
  const [orders, setOrders] = useState([]);
  const [requests, setRequests] = useState([]);
  const [terms, setTerms] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [revising, setRevising] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteMsg, setQuoteMsg] = useState("");
  const [care, setCare] = useState(null);
  const [careLoading, setCareLoading] = useState(null);
  const [blueprintModel, setBlueprintModel] = useState(null);

  const load = async () => {
    try {
      const [b, p, o, t, r, c] = await Promise.all([
        api.get("/build/blueprint"), api.get("/build/pricing"),
        api.get("/build/orders"), api.get("/build/terms"), api.get("/build/requests"),
        api.get("/care/plans"),
      ]);
      setBp(b.data.blueprint); setPaid(b.data.paid); setPricing(p.data.packages);
      setOrders(o.data.orders); setTerms(t.data); setRequests(r.data.requests);
      setCare(c.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Arriving from the voice interview review with ?order=1: jump to the build packages.
  useEffect(() => {
    if (loading || !bp) return;
    if (new URLSearchParams(location.search).get("order") === "1") {
      const el = document.getElementById("managed-builds");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      toast.info(bp.status === "SCOPE_APPROVED" ? "Pick a build below to get started." : "Approve your scope, then pick a build below.");
    }
  }, [loading, bp, location.search]);

  const generate = async () => {
    setGenerating(true);
    track("Blueprint Requested", { model: blueprintModel });
    try {
      const { data } = await api.post("/build/blueprint/generate", { model: blueprintModel });
      setBp(data);
      toast.success("Your Build Blueprint is ready.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate the blueprint. Try again."); }
    finally { setGenerating(false); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post(`/build/blueprint/${bp.blueprint_id}/approve`);
      setBp(data); toast.success("Scope approved and locked.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve."); }
    finally { setApproving(false); }
  };

  const checkout = async (pkgId) => {
    if (!agreed) { toast.error("Please accept the terms and refund policy first."); setShowTerms(true); return; }
    setCheckingOut(true);
    track("Upgrade Clicked", { package_id: pkgId });
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkgId, blueprint_id: bp?.blueprint_id, origin_url: window.location.origin,
        accepted_terms: true,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout."); setCheckingOut(false); }
  };

  const payWithPaypal = async (pkgId) => {
    if (!agreed) { toast.error("Please accept the terms and refund policy first."); setShowTerms(true); return; }
    setCheckingOut(true);
    track("Upgrade Clicked", { package_id: pkgId, provider: "paypal" });
    try {
      const { data } = await api.post("/payments/paypal/create-order", {
        package_id: pkgId, blueprint_id: bp?.blueprint_id, origin_url: window.location.origin, accepted_terms: true,
      });
      window.location.href = data.approve_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start PayPal checkout."); setCheckingOut(false); }
  };

  const requestRevision = async (orderId) => {
    setRevising(true);
    try {
      await api.post(`/build/orders/${orderId}/request-revision`);
      toast.success("Revision requested. Our team will follow up.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not request revision."); }
    finally { setRevising(false); }
  };

  const requestBusinessSite = async () => {
    setRequesting("business_site");
    try {
      await api.post("/build/requests", { package_id: "business_site", blueprint_id: bp?.blueprint_id });
      toast.success("Request sent. A founder will review your scope and set your price.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send your request."); }
    finally { setRequesting(null); }
  };

  const requestQuote = async () => {
    setRequesting("full_stack_beta");
    try {
      await api.post("/build/requests", { package_id: "full_stack_beta", blueprint_id: bp?.blueprint_id, message: quoteMsg });
      toast.success("Quote request sent. We'll reach out to schedule a discovery session.");
      setQuoteOpen(false); setQuoteMsg("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send your request."); }
    finally { setRequesting(null); }
  };

  const subscribeCare = async (plan) => {
    setCareLoading(plan);
    track("Care Plan Clicked", { plan });
    try {
      const { data } = await api.post("/care/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout."); setCareLoading(null); }
  };

  const cancelCare = async () => {
    try {
      await api.post("/care/cancel");
      toast.success("Your care plan will cancel at the end of the current billing period.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel your plan."); }
  };

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      await downloadServerFile(`/build/blueprint/${bp.blueprint_id}/export.${fmt}`, `build-blueprint.${fmt}`);
      track("Blueprint Downloaded", { fmt });
    } catch { toast.error("Download failed."); }
    finally { setDownloading(null); }
  };

  if (loading) return <FullPageLoader label="Opening Build Studio…" />;

  const bpPro = pricing.find((p) => p.id === "blueprint_pro");
  const approved = bp?.status === "SCOPE_APPROVED";
  const buildOrders = orders.filter((o) => o.package_kind === "build" && o.payment_status === "paid");
  const reqFor = (id) => requests.find((r) => r.package_id === id);

  const STATUS_STEPS = [
    { key: "in_production", label: "In production" },
    { key: "preview_ready", label: "Preview ready" },
    { key: "revision_requested", label: "Revision requested" },
    { key: "completed", label: "Completed" },
  ];

  const TermsGate = () => (
    <div className="mt-6 rounded-xl border border-brand-line bg-white p-4" data-testid="terms-gate">
      <label className="flex cursor-pointer items-start gap-2.5 text-sm text-slate-600">
        <input type="checkbox" data-testid="accept-terms-checkbox" checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand" />
        <span>I agree to the{" "}
          <button type="button" onClick={() => setShowTerms((v) => !v)} className="font-medium text-brand underline" data-testid="view-terms-btn">
            terms of service and refund policy
          </button>.
        </span>
      </label>
      {showTerms && terms && (
        <div className="mt-3 space-y-3 rounded-lg bg-brand-surface/40 p-4" data-testid="terms-body">
          <p className="text-xs text-slate-400">Version {terms.version}</p>
          {terms.sections.map((s, i) => (
            <div key={i}>
              <p className="text-sm font-semibold text-brand-text">{s.title}</p>
              <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-4xl px-8 py-10 ltifade" data-testid="build-studio">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">
          <Sparkles size={14} /> Build Studio
        </div>
        <h1 className="mt-1.5 font-heading text-2xl font-semibold tracking-tight">Turn your blueprint into a real product</h1>
        <p className="mt-2 max-w-2xl text-slate-600">
          Build Studio looks at your finished assets and recommends the smallest first product worth building.
          Review the scope, approve it, and get a developer-ready spec. Your assets and manual editing stay free.
        </p>

        {!bp ? (
          <div className="mt-8 rounded-2xl border border-brand-line bg-white p-10 text-center shadow-soft" data-testid="blueprint-empty">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-brand-surface"><Layers size={26} className="text-brand" /></span>
            <p className="mt-4 font-heading text-lg font-medium">Generate your Build Blueprint</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">We use your completed roadmap to recommend the right first product. Nothing to pay to see the recommendation.</p>
            <button data-testid="generate-blueprint-btn" onClick={generate} disabled={generating}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
              {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {generating ? "Reading your business…" : "Generate my Build Blueprint"}
            </button>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-xs text-slate-400">Model</span>
              <ModelPicker value={blueprintModel} onChange={setBlueprintModel} disabled={generating} />
            </div>
          </div>
        ) : (
          <>
            {/* Recommendation */}
            <div className="mt-8 rounded-2xl border border-brand-line bg-brand-surface/40 p-6 shadow-soft" data-testid="blueprint-recommendation">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Recommended product</p>
                  <p className="mt-1 font-heading text-xl font-semibold">{PRODUCT_LABEL[bp.product_type] || bp.product_type}</p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-medium capitalize text-brand-text shadow-soft">
                  {bp.confidence} confidence
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{bp.product_reason}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {approved ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700" data-testid="scope-locked">
                    <ShieldCheck size={14} /> Scope approved & locked (v{bp.version})
                  </span>
                ) : (
                  <>
                    <button data-testid="approve-scope-btn" onClick={approve} disabled={approving}
                      className="inline-flex items-center gap-2 rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50">
                      {approving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Approve this scope
                    </button>
                    <button data-testid="regenerate-blueprint-btn" onClick={generate} disabled={generating}
                      className="inline-flex items-center gap-2 rounded-lg border border-brand-line bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-all duration-200 hover:bg-slate-50 disabled:opacity-50">
                      {generating ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Regenerate
                    </button>
                  </>
                )}
                <button data-testid="start-voice-interview-btn" onClick={() => navigate(`/build/voice?bp=${bp.blueprint_id}`)}
                  className="inline-flex items-center gap-2 rounded-lg border border-brand bg-white px-4 py-2 text-sm font-medium text-brand transition-all duration-200 hover:bg-brand-surface/40">
                  <Mic size={15} /> Refine with a voice interview
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <Section icon={Layers} title="Pages" items={bp.pages} />
              <Section icon={Sparkles} title="Features" items={bp.features} />
              <Section icon={CircleDollarSign} title="Integrations" items={bp.integrations} />
              <Section icon={ListChecks} title="Acceptance criteria" items={bp.acceptance_criteria} locked={!paid} />
              <Section icon={Ban} title="Exclusions" items={bp.exclusions} />
              <Section icon={HelpCircle} title="Decisions you must make" items={bp.required_decisions} />
            </div>

            {/* Your managed builds status */}
            {buildOrders.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="your-builds">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Your builds</p>
                {buildOrders.map((o) => {
                  const idx = STATUS_STEPS.findIndex((s) => s.key === o.build_status);
                  return (
                    <div key={o.order_id} className="rounded-2xl border border-brand-line bg-white p-5 shadow-soft" data-testid={`build-order-${o.order_id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-heading text-lg font-medium">{o.package_name}</p>
                        <span className="rounded-full bg-brand-surface px-2.5 py-1 text-xs font-medium capitalize text-brand-text">{(o.build_status || "in production").replace("_", " ")}</span>
                      </div>
                      <div className="mt-4 flex items-center">
                        {STATUS_STEPS.map((s, i) => (
                          <div key={s.key} className="flex flex-1 items-center last:flex-none">
                            <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${i <= idx ? "bg-brand text-white" : "bg-brand-surface text-slate-400"}`}>{i < idx ? "✓" : i + 1}</span>
                            {i < STATUS_STEPS.length - 1 && <span className={`h-0.5 flex-1 ${i < idx ? "bg-brand" : "bg-brand-surface"}`} />}
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 flex justify-between text-[11px] text-slate-400">
                        {STATUS_STEPS.map((s) => <span key={s.key} className="flex-1 text-center first:text-left last:text-right">{s.label}</span>)}
                      </div>
                      {o.delivery_notes && <p className="mt-3 rounded-lg bg-brand-surface/40 p-3 text-sm text-slate-600">{o.delivery_notes}</p>}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {o.preview_url && (
                          <a href={o.preview_url} target="_blank" rel="noreferrer" data-testid={`preview-link-${o.order_id}`}
                            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover">
                            View preview <ArrowRight size={14} />
                          </a>
                        )}
                        {o.build_status === "preview_ready" && (o.revision_count || 0) < 1 && (
                          <button data-testid={`request-revision-${o.order_id}`} onClick={() => requestRevision(o.order_id)} disabled={revising}
                            className="inline-flex items-center gap-2 rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                            {revising ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Request my one revision
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {(!paid || pricing.some((p) => p.kind === "build" && p.purchasable)) && <TermsGate />}

            {/* Blueprint Pro / download */}
            {paid ? (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6" data-testid="blueprint-paid">
                <p className="flex items-center gap-2 font-heading text-lg font-medium text-emerald-800"><Check size={18} /> Blueprint Pro unlocked</p>
                <p className="mt-1 text-sm text-emerald-700">Download your developer-ready specification. Credited toward a build.</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button data-testid="download-blueprint-pdf" onClick={() => download("pdf")} disabled={downloading}
                    className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
                    {downloading === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF
                  </button>
                  <button data-testid="download-blueprint-md" onClick={() => download("md")} disabled={downloading}
                    className="inline-flex items-center gap-2 rounded-lg border border-brand-line bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    <FileText size={15} /> Markdown
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-brand-line bg-white p-6 shadow-soft" data-testid="blueprint-pro-upsell">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="max-w-md">
                    <p className="font-heading text-lg font-medium">{bpPro?.name || "Blueprint Pro"}</p>
                    <p className="mt-1 text-sm text-slate-600">{bpPro?.description}</p>
                    <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-400"><Lock size={12} /> Acceptance criteria preview is blurred until you upgrade. Your existing assets stay free.</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-2xl font-semibold">${bpPro?.amount}</p>
                    <div className="mt-2 flex flex-col items-end gap-1.5">
                      <button data-testid="buy-blueprint-pro-btn" onClick={() => checkout("blueprint_pro")} disabled={checkingOut || !approved || !agreed}
                        className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
                        {checkingOut ? <Loader2 size={15} className="animate-spin" /> : <CircleDollarSign size={15} />}
                        {approved ? "Pay with card" : "Approve scope first"}
                      </button>
                      {approved && (
                        <button data-testid="buy-blueprint-pro-paypal-btn" onClick={() => payWithPaypal("blueprint_pro")} disabled={checkingOut || !agreed}
                          className="inline-flex items-center gap-2 rounded-lg border border-[#003087] bg-[#ffc439] px-5 py-2 text-sm font-semibold text-[#003087] transition-all duration-200 hover:brightness-95 disabled:opacity-50">
                          Pay with PayPal
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Managed build packages */}
            <div className="mt-6" id="managed-builds" data-testid="managed-builds">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Have us build it for you</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {pricing.filter((p) => p.kind === "build" || p.kind === "quote").map((p) => {
                  const ordered = buildOrders.some((o) => o.package_id === p.id);
                  const req = reqFor(p.id);
                  const hasCredit = p.credit > 0 && p.purchasable;
                  return (
                  <div key={p.id} className="rounded-xl border border-dashed border-brand-line bg-white p-5" data-testid={`managed-pkg-${p.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium">{p.name}</p>
                      <div className="text-right">
                        {p.kind === "quote" ? (
                          <span className="text-sm font-semibold text-slate-600">From ${p.minimum?.toLocaleString()}</span>
                        ) : p.id === "business_site" && !p.quoted_price ? (
                          <span className="text-xs text-slate-400">Price set after scope review</span>
                        ) : (
                          <div data-testid={`price-${p.id}`}>
                            {hasCredit && <span className="mr-1 text-xs text-slate-400 line-through">${p.amount}</span>}
                            <span className="text-sm font-semibold text-brand-deep">${p.net_amount}</span>
                          </div>
                        )}
                        {p.tier === "early_access" && p.kind !== "quote" && (
                          <p className="text-[10px] font-medium text-emerald-600" data-testid={`early-access-${p.id}`}>Early access price</p>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">{p.description}</p>
                    {hasCredit && (
                      <p className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`credit-note-${p.id}`}>
                        <Check size={12} /> ${p.credit} Blueprint Pro credit applied
                      </p>
                    )}

                    {ordered ? (
                      <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600"><Check size={13} /> Ordered</p>
                    ) : p.id === "launch_page" ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button data-testid="order-launch_page-btn" onClick={() => checkout(p.id)} disabled={checkingOut || !approved || !agreed}
                          className="inline-flex items-center gap-2 rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50">
                          {checkingOut ? <Loader2 size={14} className="animate-spin" /> : <CircleDollarSign size={14} />}
                          {approved ? "Pay with card" : "Approve scope first"}
                        </button>
                        {approved && (
                          <button data-testid="order-launch_page-paypal-btn" onClick={() => payWithPaypal(p.id)} disabled={checkingOut || !agreed}
                            className="inline-flex items-center gap-2 rounded-lg border border-[#003087] bg-[#ffc439] px-4 py-2 text-sm font-semibold text-[#003087] transition-all duration-200 hover:brightness-95 disabled:opacity-50">
                            PayPal
                          </button>
                        )}
                      </div>
                    ) : p.id === "business_site" ? (
                      p.quoted_price ? (
                        <div className="mt-3" data-testid="business-site-approved">
                          <p className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><ShieldCheck size={13} /> Scope approved. Your price is ${p.net_amount}.</p>
                          <div className="flex flex-wrap gap-2">
                            <button data-testid="order-business_site-btn" onClick={() => checkout(p.id)} disabled={checkingOut || !agreed}
                              className="inline-flex items-center gap-2 rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50">
                              {checkingOut ? <Loader2 size={14} className="animate-spin" /> : <CircleDollarSign size={14} />} Pay with card
                            </button>
                            <button data-testid="order-business_site-paypal-btn" onClick={() => payWithPaypal(p.id)} disabled={checkingOut || !agreed}
                              className="inline-flex items-center gap-2 rounded-lg border border-[#003087] bg-[#ffc439] px-4 py-2 text-sm font-semibold text-[#003087] transition-all duration-200 hover:brightness-95 disabled:opacity-50">
                              PayPal
                            </button>
                          </div>
                        </div>
                      ) : req && req.status === "requested" ? (
                        <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-amber-600" data-testid="business-site-pending"><Loader2 size={13} className="animate-spin" /> Scope under review</p>
                      ) : req && req.status === "declined" ? (
                        <p className="mt-3 text-xs font-medium text-red-600" data-testid="business-site-declined">Scope not approved.{req.founder_notes ? ` ${req.founder_notes}` : ""}</p>
                      ) : (
                        <button data-testid="request-business_site-btn" onClick={requestBusinessSite} disabled={requesting === "business_site" || !approved}
                          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50">
                          {requesting === "business_site" ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                          {approved ? "Request a scope review" : "Approve scope first"}
                        </button>
                      )
                    ) : p.id === "full_stack_beta" ? (
                      req ? (
                        <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-muted" data-testid="full-stack-requested"><Check size={13} /> Quote requested. We'll be in touch to schedule a discovery session.</p>
                      ) : quoteOpen ? (
                        <div className="mt-3">
                          <textarea data-testid="quote-message" value={quoteMsg} onChange={(e) => setQuoteMsg(e.target.value)} rows={2}
                            placeholder="Tell us briefly what you want to build (optional)"
                            className="w-full rounded-lg border border-brand-line px-3 py-2 text-sm" />
                          <div className="mt-2 flex gap-2">
                            <button data-testid="submit-quote-btn" onClick={requestQuote} disabled={requesting === "full_stack_beta"}
                              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-hover disabled:opacity-50">
                              {requesting === "full_stack_beta" ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Send request
                            </button>
                            <button onClick={() => setQuoteOpen(false)} className="rounded-lg border border-brand-line px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button data-testid="request-full_stack_beta-btn" onClick={() => setQuoteOpen(true)}
                          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-brand-deep px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:opacity-90">
                          <ArrowRight size={14} /> Request a quote
                        </button>
                      )
                    ) : null}
                  </div>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-slate-400">Managed builds are delivered by the Launch to Income team. Pricing, revisions, and terms are finalized before any payment.</p>
            </div>

            {/* Care Plans (recurring maintenance) — only for customers with a purchased build */}
            {care && (care.eligibility?.launch_care || care.eligibility?.business_care || care.current) && (
              <div className="mt-6" data-testid="care-plans">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                  <HeartPulse size={14} /> Keep it running · Care Plans
                </p>
                {care.current && ["active", "past_due"].includes(care.current.status) ? (
                  <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="care-current">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="flex items-center gap-2 font-heading text-lg font-medium text-emerald-800">
                          <Check size={18} /> {care.current.plan_name} active
                        </p>
                        <p className="mt-1 text-sm text-emerald-700">
                          ${care.current.amount}/month.{" "}
                          {care.current.cancel_at_period_end
                            ? "Cancels at the end of the current billing period."
                            : "Renews automatically each month. Cancel anytime."}
                        </p>
                      </div>
                      {!care.current.cancel_at_period_end && (
                        <button data-testid="cancel-care-btn" onClick={cancelCare}
                          className="rounded-lg border border-brand-line bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-all duration-200 hover:bg-slate-50">
                          Cancel plan
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {care.plans.map((pl) => {
                        const eligible = care.eligibility?.[pl.id];
                        return (
                          <div key={pl.id} className="flex flex-col rounded-2xl border border-brand-line bg-white p-5 shadow-soft" data-testid={`care-plan-${pl.id}`}>
                            <div className="flex items-baseline justify-between">
                              <p className="flex items-center gap-2 font-heading text-lg font-medium">
                                {pl.name}
                                {pl.recommended && <span className="rounded-full bg-brand-surface px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand" data-testid={`recommended-${pl.id}`}>Recommended</span>}
                              </p>
                              <p className="font-heading text-xl font-semibold text-brand-deep">${pl.amount}<span className="text-sm font-normal text-slate-400">/mo</span></p>
                            </div>
                            <p className="mt-0.5 text-sm text-slate-500">{pl.tagline}{pl.id === "business_care" ? ":" : ""}</p>
                            <ul className="mt-3 flex-1 space-y-1.5">
                              {pl.features.map((f, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check size={14} className="mt-0.5 shrink-0 text-emerald-500" /> {f}</li>
                              ))}
                            </ul>
                            <button data-testid={`subscribe-${pl.id}-btn`} onClick={() => subscribeCare(pl.id)} disabled={!eligible || careLoading}
                              className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
                              {careLoading === pl.id ? <Loader2 size={15} className="animate-spin" /> : <HeartPulse size={15} />}
                              {eligible ? `Subscribe · $${pl.amount}/mo` : "Available after your build"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs text-slate-400" data-testid="care-note">{care.note}</p>
                    <p className="mt-1 text-xs text-slate-400">{care.billing}</p>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
